import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    ADD_NEW_CARD,
    AnnualPolicyInfo,
    AnnualPolicyPremiumTypeEnum,
    FalconNavigatorHeader,
    ICacheManager,
    ICustomerPortalService,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE, INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY_RENEWAL_PROPOSAL,
    Party,
    PaymentCard,
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_RENEWAL,
    PolicyProperty,
    PolicyPropertyEnum,
    PREMIUM_TYPE_MONTHLY,
    ProductsEnum,
    Renewal,
    Types,
    WOLF_MISSED_PAYMENT,
} from "@falcon/services";
import {PluginTemplates} from "../../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../../components/navigation/FalconHeader";
import {PaymentHistoryTable} from "../PaymentHistoryTable";
import {AutomaticRenewalCheckboxSection} from "./AutomaticRenewalCheckboxSection";
import {Redirect} from "react-router";
import {StorageSuccessModal} from "../../../../../../components/modals/StorageSuccessModal";
import {
    findMainAnnualPaymentCardByMainCardId,
    getProductByPolicyNumber,
    renderCardTypeName
} from "../../../../../../CommonFunctions";
import * as moment from "moment";
import {RouteComponentProps} from "react-router-dom";
import {AnnualProductPaymentMessageBanner} from "../../../../../../components/banners/AnnualProductPaymentMessageBanner";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../../../../annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {AnnualPaymentCardsSection} from "./AnnualPaymentCardsSection";
import {OverdueInstalmentBanner} from "../../../../../../components/banners/OverdueInstalmentBanner";
import {PaymentDaySection} from "../PaymentDaySection";
import {DirectDebitSection} from "./DirectDebitSection";
import {DirectDebitRenewalPaymentPlanOutput} from "../DirectDebitRenewalPaymentPlanOutput";

interface IAnnualPaymentDetailsPageParams {
    policyNumber: string;
}

interface IAnnualPaymentDetailsPageProps extends RouteComponentProps<IAnnualPaymentDetailsPageParams> {
    location: any;
}

interface IState {
    policy?: Policy,
    party?: Party,
    policiesLoaded: boolean,
    instalmentsInfo?: InstalmentsInfo,
    missedPaymentInfo?: InstalmentsInfo,
    partyLoaded: boolean,
    renewedPolicy: boolean,
    showMissedPaymentBanner: boolean,
    mainCardId?: string,
    isMonthlyPayerInCurrentPeriod: boolean,
    renewalProposal?: any,
    annualToMonthlyRenewal: boolean,
    allPoliciesAreCancelled: boolean,
    haveMandates: boolean,
    mandate?: any,
    reuseCard: boolean,
    propertiesLoaded: boolean,
}

@authorize
export class AnnualPaymentDetailsPage extends React.Component<IAnnualPaymentDetailsPageProps, IState> {

    private readonly pluginTemplates = new PluginTemplates();
    private referrerJourney: string | null;
    private showSuccessPopup: string | null;
    private title: string;
    private modalContent: JSX.Element;
    private anythingElseTitle: any;
    private explanationAnythingElseWording: any;
    private readonly product: ProductsEnum;

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private readonly cacheManager: ICacheManager;


    constructor(props: IAnnualPaymentDetailsPageProps) {
        super(props);

        this.state = {
            policiesLoaded: false,
            partyLoaded: false,
            propertiesLoaded: false,
            renewedPolicy: false,
            showMissedPaymentBanner: false,
            isMonthlyPayerInCurrentPeriod: false,
            annualToMonthlyRenewal: false,
            allPoliciesAreCancelled: false,
            haveMandates: false,
            reuseCard: false,
        };

        this.product = getProductByPolicyNumber(this.props.match.params.policyNumber);

        this.pluginTemplates.setPageTitle("Payment details");
    }

    public componentDidMount() {
        this.cacheManager.clearCache("getPolicies");
        this.cacheManager.clearCache("getProfile");
        this.customerPortalService.getPolicies(this.product, undefined, true)
            .subscribe((activePolicies: Policy[]) => {

                this.customerPortalService.getExpiredPolicies(this.product)
                    .subscribe(expiredPolicies => {
                        const policies = [...(activePolicies || []), ...(expiredPolicies || [])];
                        const filteredPolicy: Policy = policies
                            .filter((policy: Policy) => policy.products.includes(this.product) && policy.policyNumber === this.props.match.params.policyNumber)[0];

                        if (filteredPolicy) {
                            this.setState({
                                policy: filteredPolicy
                            });

                            const agreementVersionIds: number[] = filteredPolicy.policyHistoryList
                                .filter(ph => ph.status !== POLICY_STATUS_MTC)
                                .map(ph => ph.id);

                            this.customerPortalService.getCombinedInstalmentsInfo(agreementVersionIds).subscribe((instalmentsInfo: any) => {
                                this.setState({instalmentsInfo});
                            });

                            if (filteredPolicy.interaction?.factors?.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL &&
                                filteredPolicy.interaction?.renewalDetails?.policyAgeYears > 0) {
                                this.customerPortalService.getMissedPaymentInfo(filteredPolicy.interaction.agreementVersionId)
                                    .subscribe(
                                        (missedPaymentInfo: InstalmentsInfo) => {
                                            this.setState({missedPaymentInfo});
                                        });
                            }

                            // is policy renewed
                            const renewedPolicy: boolean = filteredPolicy.policyHistoryList.filter(ph => ph.status === POLICY_STATUS_RENEWAL).length > 0;

                            this.setState({
                                renewedPolicy
                            })
                        }


                        this.customerPortalService.isMonthlyPayerInCurrentPeriod(this.props.match.params.policyNumber).subscribe((output: boolean) => {
                            this.setState({
                                isMonthlyPayerInCurrentPeriod: output
                            })
                        })

                        if (filteredPolicy.interaction?.factors?.premiumType === AnnualPolicyPremiumTypeEnum.ANNUAL) {
                            this.customerPortalService.getRenewalProposal(filteredPolicy.policyNumber).subscribe((renewalProposal: Renewal) => {
                                if (renewalProposal?.factors.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY) {
                                    this.setState({
                                        renewalProposal,
                                        annualToMonthlyRenewal: true,
                                    })
                                }
                            })
                        }

                        const annualPolicyInfo: AnnualPolicyInfo | undefined = filteredPolicy.annualPolicyInfo || undefined;
                        let mandates = null;
                        if (annualPolicyInfo?.mandates) {
                            mandates = annualPolicyInfo.mandates.filter(
                                (entry: any) => entry.status === "NEW"
                                    || entry.status === "REQUESTED"
                                    || entry.status === "VALID");
                            this.setState({
                                haveMandates: mandates.length > 0
                            })
                            if (this.state.haveMandates) {
                                mandates.sort((firstMandate: any, secondMandate: any) => secondMandate.id - firstMandate.id);
                                this.setState({
                                    mandate: mandates[0]
                                })
                            }
                        }

                        const allPoliciesAreCancelled = activePolicies.length === 0;
                        this.setState({
                            allPoliciesAreCancelled,
                            policiesLoaded: true,
                        });
                    })

            });

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output,
                partyLoaded: true
            })
        });

        this.customerPortalService.getPolicyPropertiesByPropertyKeys(this.props.match.params.policyNumber, [PolicyPropertyEnum.POLICY_DEBT, PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD, PolicyPropertyEnum.REUSE_MAIN_CARD]).subscribe(
            (properties: PolicyProperty[]) => {
                properties.forEach(property => {
                    if (property.key === PolicyPropertyEnum.POLICY_DEBT) {
                        this.setState({showMissedPaymentBanner: property.value === "true"});
                    } else if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                        this.setState({mainCardId: property.value});
                    } else if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                        this.setState({reuseCard: property.value === "true"})
                    }
                })
                this.setState({propertiesLoaded: true})
            }
        );
    }

    public render() {
        const loading: boolean = !(this.state.policiesLoaded && this.state.partyLoaded && this.state.propertiesLoaded);
        const {policy} = this.state;

        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");
        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");

        const lastPolicyInteraction = policy ? policy.interaction : null;

        if (!loading && policy) {
            const popups = {
                [ADD_NEW_CARD]: () => this.setNewCardPopup(),
                [INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE]: () => this.setPayOffBalancePopup(policy.annualPolicyInfo.premiumType === PREMIUM_TYPE_MONTHLY),
                [WOLF_MISSED_PAYMENT]: () => this.setMissedRenewalPaymentPopup(),
                [INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY]: () => this.setPaymentDayChangePopup(),
                [INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY_RENEWAL_PROPOSAL]: () => this.setPaymentDayChangePopup(),
            };

            const matchingPopup = popups[this.referrerJourney!];

            if (matchingPopup) {
                matchingPopup();
            } else {
                window.sessionStorage.removeItem("showPopup");
                window.sessionStorage.removeItem("referrerJourney");
            }

            if (policy.annualPolicyInfo.premiumType === PREMIUM_TYPE_MONTHLY || Boolean(this.state.isMonthlyPayerInCurrentPeriod)) {
                return <Redirect to={`/dashboard/monthlyPaymentHistory/${this.props.match.params.policyNumber}`}/>
            }
        }

        const activePolicy = moment(policy?.endDate).isSameOrAfter(moment()) ? policy : null;
        const backLinkUrl: string = this.product === ProductsEnum.HOME ?
            `/dashboard/annualHome/${this.props.match.params.policyNumber}` :
            `/dashboard/annualMotor/${this.props.match.params.policyNumber}`;

        const annualPolicyInfo = policy?.annualPolicyInfo!;

        const isPopupsContentPopulated: boolean = this.modalContent != null && this.title != null;

        return (
            <div className="dashboard payment-details">

                {isPopupsContentPopulated && this.showSuccessPopup &&
                    <StorageSuccessModal
                        title={this.title}
                        explanationWording={this.modalContent}
                        anythingElseTitle={this.anythingElseTitle}
                        explanationAnythingElseWording={this.explanationAnythingElseWording}
                    />
                }

                <FalconHeader/>


                <div className="plugin">
                    <FalconNavigatorHeader
                        back={backLinkUrl}
                        title="Payment details"
                    />

                    {lastPolicyInteraction != null && (
                        <div className="inner-container">
                            <AnnualProductPaymentMessageBanner
                                showInfoModal={false}
                                interaction={lastPolicyInteraction}
                                showMissedPaymentBanner={this.state.showMissedPaymentBanner}
                                instalmentsInfo={this.state.missedPaymentInfo ?? this.state.instalmentsInfo}
                                amount={annualPolicyInfo.overdueAnnualInvoiceAmount}
                            />
                        </div>
                    )}
                    {annualPolicyInfo?.overdueMonthlyInvoiceAmount != null
                        && +annualPolicyInfo.overdueMonthlyInvoiceAmount > 0 && (
                        <OverdueInstalmentBanner amount={annualPolicyInfo.overdueMonthlyInvoiceAmount}
                                                 unpaidInstallmentIdToAmount={annualPolicyInfo.unpaidInstallmentIdToAmount}
                                                 policyNumber={lastPolicyInteraction.agreementNumber}
                                                 product={ProductsEnum.WOLF_MOTOR}
                                                reuseCard={this.state.reuseCard}/>
                    )}

                    <div className="panel bg-white overflow-hidden p-0 mx-3 font-weight-extra-light">
                        <div className="inner-content pb-4 payment-details-table">
                            <div className="pt-4 pb-2 grid-col-1-2 padding-0">
                                <AnnualPaymentCardsSection policyNumber={this.props.match.params.policyNumber} payment="annual"/>
                            </div>
                            {!this.state.renewedPolicy &&
                                <>
                                    <div className="dashed-10"/>
                                    <PaymentHistoryTable
                                        subscription={undefined}
                                        showProduct={this.product}
                                        paymentPeriod={PaymentHistoryTable.PaymentPeriod.all}
                                        history={this.props.history}
                                        location={this.props.location}
                                        match={this.props.match}/>
                                </>
                            }

                            {this.state.renewedPolicy &&
                                <>
                                    <div className="dashed-10"/>
                                    <PaymentHistoryTable
                                        title="Existing policy payment history"
                                        subscription={undefined}
                                        showProduct={this.product}
                                        expanded={true}
                                        paymentPeriod={PaymentHistoryTable.PaymentPeriod.current}
                                        history={this.props.history}
                                        location={this.props.location}
                                        match={this.props.match}/>
                                    <div className="dashed-10"/>
                                    <PaymentHistoryTable
                                        title="Old policies payment history"
                                        subscription={undefined}
                                        showProduct={this.product}
                                        paymentPeriod={PaymentHistoryTable.PaymentPeriod.past}
                                        history={this.props.history}
                                        location={this.props.location}
                                        match={this.props.match}/>
                                </>
                            }

                            {this.state.policiesLoaded && this.state.annualToMonthlyRenewal && this.state.renewalProposal &&
                                <>
                                    <div className="dashed-10"/>
                                    <PaymentDaySection
                                        allPoliciesAreCancelled={this.state.allPoliciesAreCancelled}
                                        interaction={this.state.renewalProposal}
                                        paymentType="annual-product-monthly-payments"
                                        paymentDay={this.state.haveMandates ? this.state.renewalProposal.paymentDetails.directDebitDetails.paymentDay : this.state.renewalProposal.paymentDay.day}
                                        canChangePaymentDate={true}
                                        renewalPaymentFailed={false}
                                        annualToMonthly={true}/>
                                </>
                            }

                            {this.state.policiesLoaded && this.state.annualToMonthlyRenewal && this.state.mandate && this.state.renewalProposal &&
                                <>
                                    <div className="dashed-10"/>
                                    <DirectDebitSection haveActiveMandate={this.state.haveMandates}
                                                        policyNumber={this.props.match.params.policyNumber}
                                                        interaction={this.state.renewalProposal}
                                                        mandate={this.state.mandate}
                                                        annualToMonthly={true}/>
                                </>
                            }

                            {this.state.policiesLoaded && this.state.annualToMonthlyRenewal && this.state.mandate && this.state.renewalProposal &&
                                <>
                                    <div className="dashed-10"/>
                                    <div id="paymentScheduleTable">
                                        <DirectDebitRenewalPaymentPlanOutput
                                            interaction={this.state.renewalProposal}/>
                                    </div>
                                </>
                            }
                        </div>
                    </div>

                    {activePolicy &&
                        <AutomaticRenewalCheckboxSection
                            history={this.props.history}
                            activeAnnualPolicy={activePolicy}
                            product={this.product}
                        />
                    }
                    {policy && this.pluginTemplates.renderBackLink("Back", false, backLinkUrl)}
                </div>
                <FalconFooter/>
                <Overlay show={loading}/>
            </div>
        );
    }

    private setNewCardPopup(): void {
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        if (paymentCard) {
            this.title = "All done!";
            this.modalContent =
                <p className="font-weight-bold">
                    You have a new payment method, {renderCardTypeName(paymentCard).toUpperCase()} card ending
                    in {paymentCard.cardNumberObfuscated.split('******')[1]}
                </p>;
            this.anythingElseTitle = "";
            this.explanationAnythingElseWording = "";
        }
    }

    private setPayOffBalancePopup(isMotor: boolean): void {
        const productWording: string = isMotor ? "car" : "home";
        this.title = "Payment was successful";
        this.modalContent =
            <p>
                £{this.state.policy!.interaction?.factors.payOffPremium} has been taken out from your account for paying
                off your {productWording} insurance loan
            </p>;
        this.anythingElseTitle = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle();
        this.explanationAnythingElseWording = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody();
    }

    private setMissedRenewalPaymentPopup(): void {
        const paymentAmount = window.sessionStorage.getItem("paymentAmount");
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        this.title = "Payment was successful";
        this.modalContent =
            <p>
                <b>{paymentAmount}</b> has been debited from your card ending {paymentCard?.cardNumberObfuscated.split('******')[1]} to
                renew your next year's policy.
            </p>;
        this.anythingElseTitle = "Please allow a couple of minutes for the payment to be updated in your account";
        this.explanationAnythingElseWording = "Thanks for choosing Allianz for another year!";
    }

    private setPaymentDayChangePopup = (): void => {
        if (this.state.policiesLoaded && this.state.annualToMonthlyRenewal && this.state.renewalProposal != null) {
            this.title = "All done!";
            this.modalContent =
                <div>
                    <p>
                        You have a new payment day,
                        every {PluginTemplates.convertWolfPaymentDate(this.state.renewalProposal.paymentDay.day)} of the month.
                    </p>
                </div>


            this.anythingElseTitle = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle();
            this.explanationAnythingElseWording = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody();
        }
    }


}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/payments/paymentDetails/annual/AnnualPaymentDetailsPage.tsx